import { useCallback, useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Collapse, Divider, Grid, IconButton, Typography } from '@material-ui/core';
import { ExpandLessOutlined, ExpandMoreOutlined } from '@material-ui/icons';
import { formatCurrency } from "@roc/ui/utils";
import KeyInformationItem from './key-information-item';
import KeyInformationDetailItem from './key-information-detail-item';
import { WireAuthorizationDialog } from '@roc/feature-draws';
import { StandardDialog, TextField } from '@roc/ui';
import {
  Button,
  CopyText,
} from '@roc/ui';


const useStyles = makeStyles(() => ({
  outerContainer: {
    width: '100%',
    borderRadius: '8px',
    marginBottom: '4px',
    padding: '10px',
    border: '1px solid #E1E1E1',
  },
  collapseBtn: {
    color: '#00B1FE',
  },
  collapseBtnIcon: {
    fontSize: '28px',
  },
  titleContainer: {
    paddingBottom: '12px',
  },
  title: {
    fontWeight: 700,
    fontSize: '20px',
    lineHeight: '157%',
    letterSpacing: '0.1px',
    color: '#00B1FE',
  },
  details: {
    padding: '12px 0',
    '& > .MuiGrid-item:first-of-type': {
      minWidth: '371px',
      paddingRight: '20px',
    },
    '& > .MuiGrid-item:not(:first-of-type)': {
      minWidth: '362px',
      padding: '0 40px 0 20px',
      borderLeft: '1px solid #E1E1E1',
    },
    '& > .MuiGrid-item:not(:first-of-type):last-of-type': {
      padding: '0 0 0 20px',
    },
  },
  wireButton: {
    textDecoration: 'underline',
    marginLeft: '-8px',
    '&:hover': {
      backgroundColor: 'transparent',
    }
  }
}));


const CopyWireLinkContent = ({ url }) => {
  return <Grid container direction="column" spacing={2}>
    <Grid item>
      <TextField
        testId="fastrackUrl"
        variant="outlined"
        label="Wire Authorization Link"
        value={url}
        fullWidth
        disabled
      />
    </Grid>
    <Grid item style={{ textAlign: 'center' }}>
      <CopyText valueToCopy={url}>
        <Button
          variant="contained"
          color="primary"
          testId="copyWireLink"
        >
          COPY LINK
        </Button>
      </CopyText>
    </Grid>
  </Grid>
}

export default function KeyInformationCollapse({ loan, achContentFormStatus, backInformationData }) {
  const classes = useStyles();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isBankInformationModalOpen, setIsBankInformationModalOpen] = useState(false);
  const [isWireLinkModalOpen, setIsWireLinkModalOpen] = useState(false);

  const borrowers = useMemo(() => {
    const borrowers = loan?.borrowers ?? [];

    return Array.from({ length: Math.ceil(borrowers.length / 3) }, (_, i) =>
      borrowers.slice(i * 3, i * 3 + 3)
    );
  }, [loan]);

  const handleInfoLinkClip = () => {
    if (backInformationData?.bankInformation?.data) {
      setIsBankInformationModalOpen(true);
    }
    else {
      setIsWireLinkModalOpen(true);
    }
  }
  const borrowerItems = useMemo(() => {
    return borrowers.map(chunk => chunk.map(borrower => ({
      title: `Borrower: ${borrower.firstName} ${borrower.lastName}`,
      items: [{
        label: 'FICO',
        value: borrower?.medianScore ?? 'N/A',
      }, {
        label: 'Number of Loans Paid Off',
        value: borrower?.portalProjectsCompleted ?? 'N/A',
      }, {
        label: 'Number of Loans Funded',
        value: borrower?.portalProjectsActive ?? 'N/A',
      }],
    })));
  }, [borrowers]);

  const handleExpandClick = useCallback((event) => {
    event.stopPropagation();
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  const formatCurrencyItemValue = (value?: number, minimumFractionDigits = 0) => {
    return value !== undefined && value !== null ? formatCurrency(value, minimumFractionDigits) : 'N/A';
  };

  const formatPercentageItemValue = (value?: number) => {
    return value !== undefined && value !== null ? `${value}%` : 'N/A';
  };

  const informationItems = [{
    label: 'Reno Budget',
    value: formatCurrencyItemValue(loan?.aggregatedRenovationBudget),
  }, {
    label: 'Completed Renovations',
    value: formatCurrencyItemValue(loan?.aggregateSunkCost),
  }, {
    label: 'Total Reno Budget',
    value: formatCurrencyItemValue(loan?.properties?.reduce((acc, { totalRenovationBudget }) => acc + totalRenovationBudget, 0)),
  }];

  const loanTerms = [{
    label: 'Construction Holdback Funded',
    value: formatCurrencyItemValue(loan?.calculatedLoanData?.constructionHoldbackFunded),
  }, {
    label: 'Construction Holdback Funded Pct',
    value: formatPercentageItemValue(loan?.calculatedLoanData?.constructionHoldbackFundedPercent),
  }, {
    label: 'Construction Holdback Rate',
    value: formatPercentageItemValue(loan?.constructionHoldbackToRenovationBudgetPercent),
  }];

  const keyDates = [{
    label: 'Maturity Date',
    value: loan?.maturityDate ?? 'N/A',
  }, {
    label: 'Extended Maturity Date',
    value: loan?.extendedMaturityDate ?? 'N/A',
  }];

  const checks = [{
    label: 'ACH Form Signed',
    value: achContentFormStatus,
  }, {
    label: 'Payoff Status',
    value: loan?.payoffStatus ?? 'N/A',
  }, {
    label: 'Bank Info for Construction Wires',
    value: <Button className={classes.wireButton} unsetTextTransfrom testId='bank-info-draws' onClick={() => handleInfoLinkClip()} variant='text'>{backInformationData?.bankInformation?.data ? 'Open bank info' : 'Open wire auth link'}</Button>,
  }, {
    label: 'Pay String (MBA)',
    value: loan?.mbaPayString ?? 'N/A',
  }];

  return (
    <Grid className={classes.outerContainer}>
      <Grid
        container
        direction='row'
        justifyContent='space-between'
        alignItems='baseline'
        className={isExpanded ? classes.titleContainer : ''}
      >
        <Grid item>
          <Typography variant='h5' component='h5' className={classes.title}>
            Key Information
          </Typography>
        </Grid>
        {informationItems.map((item, index) => (
          <Grid item key={index}>
            <KeyInformationItem
              label={item.label}
              value={item.value}
            />
          </Grid>
        ))}
        <Grid item>
          <IconButton onClick={handleExpandClick} className={classes.collapseBtn} >
            {isExpanded ? (
              <ExpandLessOutlined className={classes.collapseBtnIcon} />
            ) : (
              <ExpandMoreOutlined className={classes.collapseBtnIcon} />
            )}
          </IconButton>
        </Grid>
      </Grid>
      <Collapse in={isExpanded} timeout='auto' unmountOnExit>
        <Divider variant='fullWidth' />
        <Grid
          container
          direction='row'
          className={classes.details}
        >
          <Grid item>
            <KeyInformationDetailItem title='Loan Terms' items={loanTerms} />
          </Grid>
          <Grid item>
            <KeyInformationDetailItem title='Key Dates' items={keyDates} />
          </Grid>
          <Grid item>
            <KeyInformationDetailItem title='Checks' items={checks} />
          </Grid>
        </Grid>
        {
          borrowerItems.map((chunk, chunkIndex) => (
            <>
              <Divider key={chunkIndex} variant='fullWidth' />
              <Grid
                key={chunkIndex}
                container
                direction='row'
                className={classes.details}
              >
                {
                  chunk.map((borrower, borrowerIndex) => (
                    <Grid item key={borrowerIndex}>
                      <KeyInformationDetailItem title={borrower.title} items={borrower.items} />
                    </Grid>
                  ))
                }
              </Grid>
            </>
          ))
        }
      </Collapse>
      <WireAuthorizationDialog open={isBankInformationModalOpen} onClose={() => setIsBankInformationModalOpen(false)} loanId={loan?.loanId} document={null} />
      <StandardDialog
        open={isWireLinkModalOpen}
        title={<span className={classes.title}>Wire Link</span>}
        maxWidth="md"
        handleClose={() => setIsWireLinkModalOpen(false)}
        dialogContent={<CopyWireLinkContent url={backInformationData?.wireAuthorizationLink?.data} />}
      />
    </Grid>
  );
}
